/**
 * Binding a texture to a StandardMaterial3D slot, the one place that knows what a Godot
 * slot needs of its texture: the sampler its view states (`textureView.ts`).
 * `useSurfaceMaterial` crosses it once, for `<StandardMaterialSlot>` and the GLB override. It
 * value-imports `three`, so `index.ts` never reaches it (ADR-0031).
 */
import * as THREE from 'three';
import { godotTextureFilterState } from '../../textures/godotTextureFilter';
import { FULL_UV_WINDOW } from '../../../r3f/uvWindow';
import { BASE_MATERIAL_3D_DEFAULTS } from '../../../godot/scene/baseMaterial3D';
import { sourceColorSpace } from '../../textures/sourceColorSpace';
import { UV2_CHANNEL } from '../../meshes/uv2';
import { bottomUpV } from '../../../godot/math/uv';
/**
 * Whether each slot's sampler carries `source_color`, which reads a texture's sRGB view: albedo
 * (`scene/resources/material.cpp:969`), emission (:1066) and detail albedo (:1137). Every other
 * sampler reads raw. Exhaustive, so a new slot fails to compile until someone reads Godot's
 * shader. `textureBinding.md` has the shader chain and every sampler.
 */
const SLOT_READS_SOURCE_COLOR = {
    albedo_texture: true,
    emission_texture: true,
    normal_texture: false,
    roughness_texture: false,
    metallic_texture: false,
    ao_texture: false,
    heightmap_texture: false,
    anisotropy_flowmap: false,
};
/**
 * The colour space `slot`'s sampler reads `texture` in. It overrides the producer's tag: sRGB only
 * for a `source_color` slot and a texture whose format has an sRGB view.
 */
export function slotColorSpace(slot, texture) {
    return slotReadsSourceColor(slot) ? sourceColorSpace(texture) : THREE.NoColorSpace;
}
/** Whether `slot`'s sampler carries `source_color`, so it reads a texture's sRGB view. */
export function slotReadsSourceColor(slot) {
    return SLOT_READS_SOURCE_COLOR[slot];
}
/**
 * The texture states of a material's slots. Only an authored filter passes: comparing against
 * Godot's default would clone every texture whose sampler state differs from it.
 */
export function materialTextureStates(scalars, anisotropicFilteringLevel) {
    const sampler = {
        filter: scalars.textureFilter === BASE_MATERIAL_3D_DEFAULTS.textureFilter
            ? undefined
            : godotTextureFilterState(scalars.textureFilter, anisotropicFilteringLevel),
        wrap: scalars.textureRepeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping,
    };
    const uv2 = { ...sampler, uv: { repeat: scalars.uv2Scale, offset: scalars.uv2Offset } };
    return {
        uv1: scalars.triplanar?.uv1
            ? projectedState(sampler)
            : { ...sampler, uv: { repeat: scalars.uv1Scale, offset: scalars.uv1Offset } },
        // Every UV2 attribute keeps Godot's top-down V, so a mesh without one samples the top-left.
        uv2: scalars.triplanar?.uv2 ? projectedState(sampler) : topDownV({ ...uv2, channel: UV2_CHANNEL }),
        uv2Slots: scalars.uv2Slots,
    };
}
/**
 * The state of a triplanar UV set: the whole texture, from its top edge. The projected position
 * carries the set's scale and offset (`material.cpp:1425-1431`), and reads no UV attribute.
 */
function projectedState(sampler) {
    return topDownV({ ...sampler, uv: FULL_UV_WINDOW });
}
/** A state that samples `1 - (v * scale + offset)`, Godot's top-down V on a bottom-up upload. */
export function topDownV(state) {
    const { repeat, offset } = state.uv;
    return {
        ...state,
        uv: { repeat: { x: repeat.x, y: -repeat.y }, offset: { x: offset.x, y: bottomUpV(offset.y) } },
    };
}
/**
 * A state on a bottom-up UV set, as a Godot mesh's first set: Godot scales and offsets V from the
 * image's top edge, so the window samples `1 - ((1 - v) * scale + offset)`.
 */
export function fromTopEdge(state) {
    const { repeat, offset } = state.uv;
    return { ...state, uv: { repeat, offset: { x: offset.x, y: bottomUpV(repeat.y + offset.y) } } };
}
/** What `slot` samples of `texture`: the sampler and window of the slot's UV set, in the slot's colour space. */
export function slotViewSampler(texture, slot, states) {
    const { filter, wrap, uv, channel } = states.uv2Slots.includes(slot) ? states.uv2 : states.uv1;
    return { colorSpace: slotColorSpace(slot, texture), filter, wrap, window: uv, channel };
}
//# sourceMappingURL=textureBinding.js.map